import { format } from "date-fns";
import moment from "moment";
import momentDurationFormatSetup from "moment-duration-format";

import { isValidNumber } from "./";

// @ts-ignore
momentDurationFormatSetup(moment);

const HOUR = "h";
const MIN = "min";
const SECOND = "s";
const UNDEFINED = " - ";

const msInSec = 1000;
const msInMin = 60 * msInSec;
const msInHours = 60 * msInMin;

export function formatLargeNumber(number: number) {
  if (number < 10000) {
    return `${number}`;
  }

  if (number < 1000000) {
    return `${(number / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  }

  if (number < 1000000000) {
    return `${(number / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  }

  if (number < 1000000000000) {
    return `${(number / 1000000000).toFixed(1).replace(/\.0$/, "")}B`;
  }

  if (number < 1000000000000000) {
    return `${(number / 1000000000000).toFixed(1).replace(/\.0$/, "")}T`;
  }

  return `${(number / 1000000000000000).toFixed(1).replace(/\.0$/, "")}P`;
}

export function formatScore(num: number) {
  return Number((num * 100).toFixed(0));
}

export const formatDuration = (
  durationMs?: number,
  hideSeconds?: boolean
): string => {
  if (!durationMs) {
    return UNDEFINED;
  }

  const hours = Math.floor(durationMs / msInHours);
  const minutes = Math.floor(durationMs / msInMin) % 60;
  const seconds = Math.floor(durationMs / msInSec) % 60;

  const displaySeconds = hideSeconds ? "" : ` ${seconds}${SECOND}`;

  if (hours) return `${hours}${HOUR} ${minutes}${MIN}${displaySeconds}`;
  if (minutes) return `${minutes}${MIN}${displaySeconds}`;
  return `${seconds}${SECOND}`;
};

/* Don't show seconds if duration >= 1 hour (display maximum 2 values) */
export const formatDurationHours = (
  durationMs?: number,
  hideSeconds?: boolean,
  numOfHoursToLocaleString?: boolean
): string => {
  if (durationMs === undefined) return UNDEFINED;

  if (durationMs === 0) return `${durationMs}${SECOND}`;

  if (durationMs < msInHours) return formatDuration(durationMs, hideSeconds);

  const hours = Math.floor(durationMs / msInHours);
  const minutes = Math.floor(durationMs / msInMin) % 60;

  if (numOfHoursToLocaleString) {
    return `${hours.toLocaleString()}${HOUR} ${minutes}${MIN}`;
  }
  return `${hours}${HOUR} ${minutes}${MIN}`;
};

export const unformatDurationHours = (duration: string) => {
  let hours;
  let mins;
  let seconds;
  const splitHours = duration.split(HOUR);

  if (splitHours.length > 1) {
    hours = Number(splitHours[0]);
    duration = splitHours[1];
  }

  const splitMin = duration.split(MIN);

  if (splitMin.length > 1) {
    mins = Number(splitMin[0]);
    duration = splitMin[1];
  }

  const splitSeconds = duration.split(SECOND);

  if (splitSeconds.length > 1) {
    seconds = Number(splitSeconds[0]);
  }

  hours = hours ? hours * 1000 * 60 * 60 : 0;
  mins = mins ? mins * 1000 * 60 : 0;
  seconds = seconds ? seconds * 1000 : 0;

  return hours + mins + seconds;
};

export const formatWithLeadingZero = (num: number): string => {
  if (num < 10) return "" + 0 + num;
  return "" + num;
};

export const formatDurationHHMMSS = (durationMs: number): string => {
  if (durationMs < 0) return "";

  const duration = moment.duration(durationMs);
  const daysAsHours = duration.days() * 24;
  const hours = formatWithLeadingZero(daysAsHours + duration.hours());
  const minutes = formatWithLeadingZero(duration.minutes());
  const seconds = formatWithLeadingZero(duration.seconds());

  return "" + hours + minutes + seconds;
};

export const getHoursAndMinutesSinceTime = (time?: string): string =>
  formatDurationHours(moment().diff(moment(time)));

export const getHoursAndMinutesUntilTime = (
  refMoment: moment.Moment,
  time: moment.Moment
): string | null | undefined => {
  const diff = moment(time).diff(refMoment);
  if (diff < 0) return null;
  return formatDurationHHMMSS(diff);
};

export const formatLargeDuration = (durationMs?: number): string => {
  if (!durationMs) {
    return UNDEFINED;
  }

  const duration = moment.duration(durationMs);

  const weeks = Math.floor(duration.asWeeks());
  if (weeks > 0) return `${weeks}w ${Math.floor(duration.asDays() % 7)}d`;

  const days = Math.floor(duration.asDays());
  if (days > 0) return `${days}d ${Math.floor(duration.hours())}h`;

  return formatDurationHours(durationMs);
};

export const formatDifferenceBetweenDates = (
  startDate: string | number | moment.Moment,
  endDate: string | number | moment.Moment
) => {
  startDate = moment(startDate);
  endDate = moment(endDate);

  function customTemplate(this: any) {
    const seconds = this.duration.asSeconds();

    if (seconds < 3600) {
      return "m [minutes]";
    } else if (seconds < 86400) {
      return "h [hours]";
    }

    return "d [days]";
  }

  const diff = Math.abs(startDate.diff(endDate, "seconds"));

  if (diff < 60) {
    return 0;
  }

  // @ts-ignore
  return moment.duration(diff, "seconds").format(customTemplate);
};

export const formatProficiency = (proficiency: number): string => {
  if (!proficiency || !isValidNumber(proficiency) || proficiency < 0) {
    return "0%";
  }

  if (proficiency > 1) {
    return "100%";
  }

  return String(Math.floor(proficiency * 100)) + "%";
};

export const capitaliseEachWord = (text?: string | null) => {
  if (typeof text !== "string") return text;
  return text.replace(
    /[A-Za-z]+(?=[\s-]*)/g,
    word => word.charAt(0).toUpperCase() + word.substr(1)
  );
};

export const splitCamelCaseWord = (text?: string | null) => {
  if (typeof text !== "string") return text;
  return text.replace(/([a-z])([A-Z])/g, "$1 $2");
};

export const formatCurrency = (
  pence: number | null | undefined,
  hidePenceIfZero?: boolean
) => {
  if (!isValidNumber(pence)) {
    return null;
  }

  const roundedPence = Math.round(pence!);

  if (roundedPence === 0) {
    return `£0.00`;
  }

  if (Math.abs(roundedPence) < 100) {
    return roundedPence > 0 ? `${roundedPence}p` : `-${roundedPence * -1}p`;
  }

  const pounds = roundedPence / 100;

  let poundsString = pounds > 0 ? pounds.toFixed(2) : (-1 * pounds).toFixed(2);

  if (
    Math.floor(pounds) === pounds &&
    (Math.abs(pounds) >= 100 || hidePenceIfZero)
  ) {
    poundsString = pounds > 0 ? String(pounds) : String(-1 * pounds);
  }

  return pounds > 0 ? `£${poundsString}` : `-£${poundsString}`;
};

const DATE_FORMAT = "Do MMMM YYYY";
export const formatDateToText = (date: moment.Moment | string): string =>
  moment(date).format(DATE_FORMAT);

export function formatDateToISO(date: Date) {
  return format(date, "yyyy-MM-dd");
}

export function formatDateToDayMonth(date: string) {
  return format(new Date(date), "do MMM");
}

/**
 * Example: 13th Jan 2025
 * returns full date text with shortened month
 * do MMM yyyy
 */
export function formatDateToTextWithShortMonth(date: Date) {
  return format(date, "do MMM yyyy");
}

/**
 * Example: 06:12 AM
 * returns HH:MM (AM/PM)
 */
export function formatDateToTime(date: Date) {
  return format(date, "p");
}

/**
 * Example: 02/11/2025
 * returns MM/dd/YYY
 */
export function formatDateWithSlash(date: Date) {
  return format(date, "MM/dd/yyyy");
}
